'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from '@boty/i18n/navigation';
import { locales, type Locale } from '@boty/i18n';
import { useAuth } from '@boty/database';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';

const LOCALE_PREFERENCE_KEY = 'boty_locale_preference';

/**
 * Sync language preference to database for authenticated users.
 * This is fire-and-forget - we don't block the UI on this.
 */
async function syncLanguageToDatabase(language: Locale): Promise<void> {
  try {
    await fetch('/api/user/language-preference', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language }),
    });
  } catch (error) {
    // Silently fail - localStorage is the source of truth for UI
    console.warn('[LanguageSwitcher] Failed to sync language to database:', error);
  }
}

const LOCALE_SHORT_LABELS: Record<string, string> = {
  en: 'EN',
  he: 'עב',
  es: 'ES',
};

export interface LanguageSwitcherProps {
  /**
   * "minimal" - Icon-only with dropdown (for landing page, minimal footprint)
   * "full" - ButtonGroup toggle "EN | עב" (for headers with more space)
   */
  variant?: 'minimal' | 'full';
}

/**
 * LanguageSwitcher - Allows users to switch between Hebrew and English
 *
 * Two variants:
 * - `minimal`: Globe icon with dropdown menu (for landing page)
 * - `full`: ButtonGroup toggle "EN | עב" (for headers)
 */
export function LanguageSwitcher({ variant = 'full' }: LanguageSwitcherProps) {
  const t = useTranslations('common.language');
  const tSwitcher = useTranslations('common.languageSwitcher');
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();

  // Menu state for minimal variant
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleLocaleChange = (newLocale: Locale) => {
    if (newLocale === currentLocale) {
      setAnchorEl(null);
      return;
    }

    // Save preference to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCALE_PREFERENCE_KEY, newLocale);
    }

    // Sync to database if user is authenticated (fire-and-forget)
    if (user) {
      syncLanguageToDatabase(newLocale);
    }

    // Navigate to same page with new locale
    // next-intl's router.push accepts path + options with locale
    router.push(pathname, { locale: newLocale });
    setAnchorEl(null);
  };

  // MINIMAL VARIANT: Globe icon with dropdown menu
  if (variant === 'minimal') {
    return (
      <>
        <Tooltip title={tSwitcher('tooltip')} arrow>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            aria-label={tSwitcher('ariaLabel')}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <LanguageIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {locales.map((locale) => (
            <MenuItem
              key={locale}
              onClick={() => handleLocaleChange(locale)}
              selected={locale === currentLocale}
            >
              {t(locale)}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  // FULL VARIANT: ButtonGroup toggle "EN | עב"
  return (
    <Tooltip title={tSwitcher('tooltip')} arrow>
      <ButtonGroup
        size="small"
        variant="text"
        aria-label={tSwitcher('ariaLabel')}
      >
        {locales.map((locale) => (
          <Button
            key={locale}
            onClick={() => handleLocaleChange(locale)}
            sx={{
              minWidth: 36,
              px: 1,
              fontWeight: locale === currentLocale ? 700 : 400,
              opacity: locale === currentLocale ? 1 : 0.6,
              textDecoration: locale === currentLocale ? 'underline' : 'none',
              textUnderlineOffset: 4,
              '&:hover': {
                opacity: 1,
              },
            }}
          >
            {LOCALE_SHORT_LABELS[locale] || locale.toUpperCase()}
          </Button>
        ))}
      </ButtonGroup>
    </Tooltip>
  );
}

export default LanguageSwitcher;
